import { useState } from 'react';
import { X, Calendar, User, Wrench, Loader2, CheckCircle, FileText, CalendarX } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { errorMessage } from '../../../../lib/errors';

interface Appointment {
    id: string;
    start_time: string;
    end_time: string;
    customer_name: string;
    customer_phone: string;
    vehicle_vrm: string;
    vehicle_make: string;
    vehicle_model: string;
    status: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
    notes: string;
    linked_job_id: string | null;
    linked_quote_id: string | null;
}

interface AppointmentDetailsModalProps {
    appointment: Appointment | null;
    isOpen: boolean;
    onClose: () => void;
    onUpdate: () => void; // Refresh calendar
}

export default function AppointmentDetailsModal({ appointment, isOpen, onClose, onUpdate }: AppointmentDetailsModalProps) {
    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [confirmingCancel, setConfirmingCancel] = useState(false);

    if (!isOpen || !appointment) return null;

    /**
     * Cancels rather than deletes. The appointment stays in the record — a
     * booking that existed and was called off is history worth keeping, and the
     * calendar hides cancelled entries by default so the slot reads as free
     * and can be re-booked immediately.
     */
    const handleCancel = async () => {
        setLoading(true);
        setErrorMsg(null);
        try {
            const { error } = await supabase
                .from('appointments')
                .update({ status: 'cancelled' })
                .eq('id', appointment.id);
            if (error) throw error;
            setSuccessMsg('Appointment cancelled — the slot is free again.');
            setTimeout(() => { onUpdate(); onClose(); }, 900);
        } catch (err) {
            setErrorMsg(err instanceof Error ? err.message : 'Failed to cancel');
        } finally {
            setLoading(false);
            setConfirmingCancel(false);
        }
    };

    const handleConvert = async (type: 'job' | 'quote') => {
        setLoading(true);
        setErrorMsg(null);
        setSuccessMsg(null);
        try {
            const rpcName = type === 'job'
                ? 'fn_convert_appointment_to_job'
                : 'fn_convert_appointment_to_quote';

            const { data: _data, error } = await supabase.rpc(rpcName, {
                p_appt_id: appointment.id,
                p_start_time_for_partition: appointment.start_time
            });

            if (error) throw error;

            setSuccessMsg(`${type === 'job' ? 'Job' : 'Quote'} created successfully!`);
            setTimeout(() => {
                onUpdate();
                onClose();
            }, 1000);
        } catch (err) {
            console.error('Conversion failed:', err);
            setErrorMsg(errorMessage(err) || 'Failed to convert');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-scrim/80 backdrop-blur-sm animate-in fade-in">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-md bg-panel border border-line rounded-xl shadow-2xl overflow-hidden"
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-tint/10 bg-card/50">
                    <h2 className="text-xl font-bold text-fg flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-accent" />
                        Appointment Details
                    </h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-tint/10 transition-colors text-fg-3 hover:text-fg">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Status Badge */}
                    <div className="flex justify-between items-start">
                        <div className="text-sm font-bold text-fg-4 uppercase tracking-wider">Status</div>
                        <div className={`
                            px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider border
                            ${appointment.status === 'scheduled' ? 'bg-info/10 border-info/20 text-info run-border-shine' : 'bg-raised border-line-strong text-fg-3'}
                        `}>
                            {appointment.status}
                        </div>
                    </div>

                    {/* Time */}
                    <div>
                        <div className="flex items-center gap-2 text-fg font-mono text-lg font-bold">
                            {format(new Date(appointment.start_time), 'HH:mm')} - {format(new Date(appointment.end_time), 'HH:mm')}
                        </div>
                        <div className="text-sm text-fg-4">
                            {format(new Date(appointment.start_time), 'EEEE, d MMMM yyyy')}
                        </div>
                    </div>

                    {/* Customer & Vehicle */}
                    <div className="p-4 bg-tint/5 rounded-lg border border-tint/5 space-y-3">
                        <div className="flex items-start gap-3">
                            <User className="w-4 h-4 text-fg-3 mt-1" />
                            <div>
                                <div className="text-sm font-bold text-fg">{appointment.customer_name}</div>
                                <div className="text-xs text-fg-3">{appointment.customer_phone}</div>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            {/* Car Icon */}
                            <div className="w-4 h-4 mt-1 flex items-center justify-center font-bold text-[10px] bg-raised text-fg-2 rounded">V</div>
                            <div>
                                <div className="text-sm font-bold text-fg uppercase">{appointment.vehicle_vrm}</div>
                                <div className="text-xs text-fg-3">{appointment.vehicle_make} {appointment.vehicle_model}</div>
                            </div>
                        </div>
                    </div>

                    {/* Notes */}
                    {appointment.notes && (
                        <div>
                            <div className="text-xs font-bold text-fg-4 uppercase mb-1">Notes</div>
                            <div className="text-sm text-fg-2 bg-card/50 p-3 rounded-lg border border-tint/5">
                                {appointment.notes}
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="pt-4 border-t border-tint/10 space-y-3">
                        {successMsg ? (
                            <div className="text-success flex items-center gap-2 justify-center py-2 bg-success/10 rounded-lg">
                                <CheckCircle className="w-4 h-4" />
                                {successMsg}
                            </div>
                        ) : errorMsg ? (
                            <div className="text-danger flex items-center gap-2 justify-center py-2 bg-danger/10 rounded-lg">
                                <X className="w-4 h-4" />
                                {errorMsg}
                            </div>
                        ) : (
                            (!appointment.linked_job_id && !appointment.linked_quote_id) ? (
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        onClick={() => handleConvert('quote')}
                                        disabled={loading}
                                        className="flex items-center justify-center gap-2 px-4 py-3 bg-raised hover:bg-pressed text-fg rounded-lg font-bold text-sm transition-all"
                                    >
                                        <FileText className="w-4 h-4 text-fg-3" />
                                        To Quote
                                    </button>
                                    <button
                                        onClick={() => handleConvert('job')}
                                        disabled={loading}
                                        className="flex items-center justify-center gap-2 px-4 py-3 fd-btn-primary rounded-lg font-bold text-sm transition-all"
                                    >
                                        <Wrench className="w-4 h-4" />
                                        To Job
                                        {loading && <Loader2 className="w-4 h-4 animate-spin ml-1" />}
                                    </button>
                                </div>
                            ) : (
                                <div className="text-center text-fg-4 text-xs py-2">
                                    {appointment.linked_job_id ? 'Linked to Job' : 'Linked to Quote'}
                                </div>
                            )
                        )}

                        {!successMsg && appointment.status === 'scheduled' && (
                            confirmingCancel ? (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setConfirmingCancel(false)}
                                        disabled={loading}
                                        className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold bg-raised hover:bg-pressed text-fg transition-colors"
                                    >
                                        Keep it
                                    </button>
                                    <button
                                        onClick={handleCancel}
                                        disabled={loading}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold bg-danger/15 border border-danger/40 text-danger hover:bg-danger/25 transition-colors"
                                    >
                                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarX className="w-4 h-4" />}
                                        Cancel booking
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setConfirmingCancel(true)}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-fg-3 hover:text-danger hover:bg-danger/10 transition-colors"
                                >
                                    <CalendarX className="w-4 h-4" />
                                    Cancel this appointment
                                </button>
                            )
                        )}
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
